/**
 * A post's stats, for its author, in two columns: engagement (reactions, replies, reposts, shares) and people
 * reaching out to you from the post (suggestions, invitations, meeting requests, messages) — each a 2 × 2 grid of
 * totals over a chart through time: engagement as running totals (sloped lines), reaching out per day or week (bars).
 * Backend: GET /interact/posts/<id>/stats/ (shares and actions are recorded by PostShare's recordPostEvent).
 *
 * Colours: the first four slots of the validated categorical palette (dataviz skill), in fixed order; aqua and
 * yellow are below 3:1 against the surface, so every value is also in the tiles above and in a hidden table.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { InsightsOutlined as StatsIcon } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { fetcher } from 'shared/helpers/axios';

const SLOTS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];       // blue, orange, aqua, yellow
const ENGAGEMENT = [['reactions', 'Reactions'], ['replies', 'Replies'], ['reposts', 'Reposts'], ['shares', 'Shares']];
const REACHING = [['suggest', 'Suggestions'], ['invite', 'Invitations'], ['meet', 'Meeting requests'], ['message', 'Messages']];
// the chart's legend is narrow: short names (the tiles above carry the full ones, in the same colours)
const REACHING_SHORT = { suggest: 'Suggestions', invite: 'Invites', meet: 'Meetings', message: 'Messages' };

const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
const shortDate = (iso, unit) => {
  const d = new Date(`${iso}T00:00:00`);
  const label = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return unit === 'week' ? `week of ${label}` : label;
};
// About five date labels, evenly spaced, the last one only when it does not crowd the one before it
const tickEvery = (n) => Math.max(1, Math.ceil(n / 5));
const showTick = (n) => (_v, i) => i % tickEvery(n) === 0 || (i === n - 1 && (n - 1) % tickEvery(n) >= tickEvery(n) * 0.6);

const CHART_SX = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(0,0,0,0.25)' },     // recessive axes
  '& .MuiChartsAxis-tickLabel': { fill: 'rgba(0,0,0,0.6)', fontSize: 10 },
  '& .MuiLineElement-root': { strokeWidth: 2 },
  '& .MuiBarElement-root': { stroke: '#fff', strokeWidth: 2 },                         // a gap between stacked fills
};

function Tile({ label, value, sub, color }) {
  return (
    <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'action.hover', minWidth: 0, position: 'relative' }}>
      {/* the series colour beside the number: the same entity in the chart below */}
      <Box sx={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>{sub || ' '}</Typography>
    </Box>
  );
}

const Grid2 = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>{children}</Box>
);

function StatsBody({ url }) {
  const { data: s, error } = useSWR(url, fetcher);
  if (error) return <Typography color="text.secondary">The stats could not be loaded.</Typography>;
  if (!s) return <Box display="grid" sx={{ placeItems: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  const shares = s.shares.link.count + s.shares.device.count + s.shares.email.count + (s.shares.message?.count || 0);
  const act = s.actions;
  const t = s.timeline || { dates: [], engagement: {}, reaching_out: {}, unit: 'day' };
  const x = t.dates.map((d) => shortDate(d, t.unit));
  const hasEngagement = ENGAGEMENT.some(([k]) => (t.engagement[k] || []).some(Boolean));
  const hasReaching = REACHING.some(([k]) => (t.reaching_out[k] || []).some(Boolean));
  const empty = (text) => (
    <Box sx={{ height: 150, display: 'grid', placeItems: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, mt: 1 }}>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
      <Box minWidth={0}>
        <Typography variant="overline" color="text.secondary">Engagement</Typography>
        <Grid2>
          <Tile label="Reactions" value={s.reactions.count} color={SLOTS[0]}
            sub={Object.entries(s.reactions.by_emoji).map(([e, n]) => `${e} ${n}`).join('  ')} />
          <Tile label="Replies" value={s.replies.count} color={SLOTS[1]} sub={s.replies.count ? people(s.replies.people) : ''} />
          <Tile label="Reposts" value={s.reposts.count} color={SLOTS[2]}
            sub={s.reposts.with_comment ? `${s.reposts.with_comment} with a comment` : ''} />
          <Tile label="Shares" value={shares} color={SLOTS[3]}
            sub={shares ? `link ${s.shares.link.count} · menu ${s.shares.device.count} · email ${s.shares.email.count} · message ${s.shares.message?.count || 0}` : ''} />
        </Grid2>
        {hasEngagement ? (
          <LineChart height={170} sx={CHART_SX} margin={{ top: 34, right: 16, bottom: 24, left: 26 }}
            xAxis={[{ scaleType: 'point', data: x, tickLabelInterval: showTick(x.length) }]}
            yAxis={[{ tickMinStep: 1 }]}
            grid={{ horizontal: true }}
            series={ENGAGEMENT.map(([k, label], i) => ({
              data: t.engagement[k] || [], label, color: SLOTS[i], curve: 'linear', showMark: false,   // sloped, not steps
            }))}
            slotProps={{ legend: { position: { vertical: 'top', horizontal: 'middle' }, itemMarkWidth: 8, itemMarkHeight: 8, itemGap: 6, labelStyle: { fontSize: 10.5 } } }}
          />
        ) : empty('No reactions, replies, reposts or shares yet')}
        {s.why_for_me > 0 && (
          <Typography variant="caption" color="text.secondary">“Why is this for me?” asked by {people(s.why_for_me)}</Typography>
        )}
      </Box>

      <Box minWidth={0}>
        <Typography variant="overline" color="text.secondary">Reaching out to you from this post</Typography>
        <Grid2>
          {REACHING.map(([k, label], i) => (
            <Tile key={k} label={label} value={act[k].count} color={SLOTS[i]} sub={act[k].count ? people(act[k].people) : ''} />
          ))}
        </Grid2>
        {hasReaching ? (
          <BarChart height={170} sx={CHART_SX} margin={{ top: 34, right: 16, bottom: 24, left: 26 }} borderRadius={4}
            xAxis={[{ scaleType: 'band', data: x, categoryGapRatio: 0.35,
              tickLabelInterval: showTick(x.length) }]}
            yAxis={[{ tickMinStep: 1 }]}
            grid={{ horizontal: true }}
            series={REACHING.map(([k], i) => ({ data: t.reaching_out[k] || [], label: REACHING_SHORT[k], color: SLOTS[i], stack: 'all' }))}
            slotProps={{ legend: { position: { vertical: 'top', horizontal: 'middle' }, itemMarkWidth: 8, itemMarkHeight: 8, itemGap: 6, labelStyle: { fontSize: 10.5 } } }}
          />
        ) : empty('Nobody has reached out from this post yet')}
      </Box>

      {/* the numbers behind both charts, for screen readers */}
      <Box sx={visuallyHidden}>
        <table>
          <caption>Post stats per {t.unit}</caption>
          <thead><tr><th>{t.unit === 'week' ? 'Week of' : 'Day'}</th>
            {ENGAGEMENT.map(([, l]) => <th key={l}>{l} (total)</th>)}{REACHING.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
          <tbody>{t.dates.map((d, i) => (
            <tr key={d}><td>{d}</td>
              {ENGAGEMENT.map(([k]) => <td key={k}>{(t.engagement[k] || [])[i]}</td>)}
              {REACHING.map(([k]) => <td key={k}>{(t.reaching_out[k] || [])[i]}</td>)}</tr>
          ))}</tbody>
        </table>
      </Box>
    </Box>
  );
}

/** The button in the author's own post's (or interest's: INTEREST) actions row, and the dialog it opens. */
export default function PostStatsButton({ post, interest = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" startIcon={<StatsIcon />} onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
        Stats
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth PaperProps={{ sx: { maxWidth: 680 } }}>
        <DialogTitle>{interest ? 'Interest stats' : 'Post stats'}</DialogTitle>
        <DialogContent dividers>
          {open && <StatsBody url={`/interact/${interest ? 'comments' : 'posts'}/${post.id}/stats/`} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
}
